// ProformaMobile.jsx — onglet « Proforma/Devis » en version mobile.
//
// Consultation + conversion en vente + suppression/restauration + PDF. La création et la
// modification d'un devis (modal desktop de ~370 lignes, avec table de lignes éditables)
// restent sur ordinateur : sur téléphone, l'action à valeur est « Convertir en vente ».
//
// Réutilise l'objet `api` de GestionProforma.tsx : aucun appel n'est réécrit ici.
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { message, Modal } from "antd";
import { useNavigate } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import { invalidateBusinessResources } from "../../utils/businessResourceFreshness";
import {
  ProductScreen, Appbar, SearchBar, Chips, EmptyState, Sheet, ActionRow,
  DetailRow, Note, Pill, Mono, Ico, fmt, fmtMoney, useMobileMenu,
} from "../produits/productMobileKit";
import { useAuth } from "../../context/AuthContext";
import { useSelection } from "../../SelectionContext";
import { api } from "../../gestion-proforma/GestionProforma";
import { getSalesTabsForUser } from "../../gestion-vente/SalesTabsLayout";
import { downloadProformaPdf } from "./proformaMobileApi";
import { PERM, actionsFor, clientNameOf, fmtDate, num, proformaPill } from "./proformaMobileShared";

const PAGE = 20;

// Le desktop n'a aucun filtre de statut branché (`SET_ACTIVE_TAB` n'est jamais dispatché) :
// on n'en invente pas. Seul le bouton « Afficher les supprimées » existe, et `deleted=1`
// renvoie les supprimées EN PLUS des actives — on ne re-filtre donc pas côté client.
const FILTERS = [
  { key: "all", label: "Tous", deleted: false },
  { key: "deleted", label: "Avec supprimés", deleted: true },
];

const idOf = (v) => (v && typeof v === "object" ? v.id : v);

export default function ProformaMobile({ onMenu: onMenuProp }) {
  const outletMenu = useMobileMenu();
  const onMenu = onMenuProp || outletMenu;
  const navigate = useNavigate();
  const { user, hasPermission } = useAuth();
  const { selectedWarehouse, selectedCompany } = useSelection();
  const queryClient = useQueryClient();

  const companyId = idOf(selectedCompany);
  const warehouseId = idOf(selectedWarehouse);
  const pageScope = JSON.stringify([user?.id, companyId, warehouseId]);
  const currentPageScope = useRef(pageScope);
  currentPageScope.current = pageScope;

  const can = {
    convert: hasPermission(PERM.convert),
    delete: hasPermission(PERM.delete),
  };

  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(false);
  const [clients, setClients] = useState([]);

  const [search, setSearch] = useState("");
  const [appliedSearch, setAppliedSearch] = useState("");
  const [filter, setFilter] = useState("all");
  const debounceRef = useRef(null);
  const listRequestRef = useRef(0);

  const [navOpen, setNavOpen] = useState(false);
  const [detail, setDetail] = useState(null);
  const [items, setItems] = useState([]);
  const [detailLoading, setDetailLoading] = useState(false);
  const [pdfBusy, setPdfBusy] = useState(false);
  const converting = useRef(false);

  const salesTabs = useMemo(
    () =>
      getSalesTabsForUser(user, selectedCompany).filter(
        (t) => hasPermission(t.permission) && !t.path.endsWith("/proforma")
      ),
    [user, selectedCompany, hasPermission]
  );

  useEffect(() => {
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => setAppliedSearch(search), 350);
    return () => clearTimeout(debounceRef.current);
  }, [search]);

  useEffect(() => {
    let alive = true;
    api
      .fetchClients(companyId, warehouseId)
      .then((c) => { if (alive) setClients(Array.isArray(c) ? c : []); })
      .catch(() => {});
    return () => { alive = false; };
  }, [companyId, warehouseId]);

  const load = useCallback(
    async (p) => {
      const requestId = ++listRequestRef.current;
      const requestScope = JSON.stringify([user?.id, companyId, warehouseId]);
      if (!warehouseId) { setRows([]); setTotal(0); setLoading(false); return; }
      const f = FILTERS.find((x) => x.key === filter) || FILTERS[0];
      setLoading(true);
      try {
        const res = await api.fetchProformas({
          page: p,
          limit: PAGE,
          warehouse_id: warehouseId,
          company_id: companyId,
          ...(appliedSearch.trim() ? { search: appliedSearch.trim() } : {}),
          ...(f.deleted ? { show_deleted: true } : {}),
        });
        const incoming = res.proformas || [];
        if (requestScope !== currentPageScope.current || requestId !== listRequestRef.current) return;
        setRows((prev) => (p === 1 ? incoming : [...prev, ...incoming]));
        setTotal(res.total || 0);
      } catch {
        if (requestScope === currentPageScope.current && requestId === listRequestRef.current) {
          message.error("Impossible de charger les devis");
        }
      } finally {
        if (requestScope === currentPageScope.current && requestId === listRequestRef.current) setLoading(false);
      }
    },
    [user?.id, companyId, warehouseId, appliedSearch, filter]
  );

  useEffect(() => {
    setPage(1);
    load(1);
    return () => { listRequestRef.current += 1; };
  }, [load]);

  const loadMore = () => { const p = page + 1; setPage(p); load(p); };
  const reload = () => { setPage(1); load(1); };

  /* ---------- détail ---------- */
  const openDetail = async (record) => {
    setDetail(record);
    setItems([]);
    setDetailLoading(true);
    try {
      const [full, lignes] = await Promise.all([
        api.fetchProformaDetail(String(record.id)).catch(() => record),
        api.fetchOrderItems(String(record.id)).catch(() => []),
      ]);
      setDetail({ ...record, ...full });
      setItems(Array.isArray(lignes) ? lignes : []);
    } finally {
      setDetailLoading(false);
    }
  };
  const closeDetail = () => { setDetail(null); setItems([]); };

  /* ---------- actions ---------- */
  // Le contexte d'entreprise est explicite, puis la vente créée peut être ouverte.
  const convert = (record) => {
    Modal.confirm({
      title: "Convertir ce devis en vente ?",
      content: "Une commande de vente sera créée à partir de ce devis.",
      centered: true, okText: "Oui, convertir", cancelText: "Non",
      onOk: async () => {
        if (converting.current) return;
        converting.current = true;
        try {
          const result = await api.convertToSale(record.id, record.company_id);
          void invalidateBusinessResources(queryClient, {
            userId: user?.id, companyId: record.company_id, warehouseId: record.warehouse_id,
          }, "sale");
          message.success(result.message || "Devis converti en vente");
          closeDetail();
          reload();
          Modal.confirm({
            title: "Vente créée",
            content: `Facture ${result.invoice_number || ""} — souhaitez-vous accéder à la vente créée ?`,
            centered: true, okText: "Oui", cancelText: "Non",
            onOk: () => navigate(`/gestion-ventes/ventes?id=${result.orderId || result.id}`),
          });
        } catch (err) {
          message.error(err?.message || "Erreur lors de la conversion");
        } finally {
          converting.current = false;
        }
      },
    });
  };

  const runDestructive = (record, kind) => {
    const spec = {
      delete: {
        title: "Supprimer ce devis ?", ok: "Oui, supprimer", danger: true,
        run: () => api.deleteProforma(record.id), done: "Devis supprimé avec succès",
      },
      restore: {
        title: "Restaurer ce devis ?", ok: "Oui, restaurer", danger: false,
        run: () => api.restoreProforma(record.id), done: "Devis restauré avec succès",
      },
    }[kind];
    if (!spec) return;

    closeDetail();
    Modal.confirm({
      title: spec.title, centered: true, okText: spec.ok, cancelText: "Non",
      okType: spec.danger ? "danger" : "primary",
      onOk: async () => {
        try { await spec.run(); message.success(spec.done); reload(); }
        catch { message.error("L'opération a échoué"); }
      },
    });
  };

  const downloadPdf = async (record) => {
    if (pdfBusy) return;
    setPdfBusy(true);
    message.loading({ content: "Préparation du devis…", key: "proforma-pdf" });
    try {
      await downloadProformaPdf(record, clients);
      message.success({ content: "Devis généré avec succès", key: "proforma-pdf" });
    } catch (err) {
      message.error({ content: err?.message || "Erreur lors de la génération du PDF", key: "proforma-pdf" });
    } finally {
      setPdfBusy(false);
    }
  };

  /* ---------- rendu ---------- */
  if (!warehouseId) {
    return (
      <ProductScreen>
        <Appbar title="Devis" onMenu={onMenu} />
        <div className="body">
          <EmptyState icon="warehouse" title="Aucun établissement sélectionné"
            text="Choisissez un établissement pour voir les devis." />
        </div>
      </ProductScreen>
    );
  }

  const actions = detail ? actionsFor(detail, can) : {};

  return (
    <ProductScreen>
      <Appbar
        title="Devis" onMenu={onMenu}
        subtitle={`${fmt(total)} proforma${total > 1 ? "s" : ""}`}
        action={
          salesTabs.length ? (
            <button className="iconbtn" aria-label="Aller à un autre onglet" onClick={() => setNavOpen(true)}>
              <Ico name="list-ordered" />
            </button>
          ) : null
        }
      />

      <div className="appbar" style={{ borderTop: 0, paddingTop: 0, gap: 12 }}>
        <SearchBar value={search} onChange={setSearch} placeholder="Rechercher un devis ou un client…" />
        <Chips value={filter} onChange={setFilter} options={FILTERS.map((f) => ({ key: f.key, label: f.label }))} />
      </div>

      <div className="body">
        {loading && !rows.length ? (
          <EmptyState loading title="Chargement des devis" text="Synchronisation en cours." />
        ) : !rows.length ? (
          <EmptyState
            icon="file-text"
            title={appliedSearch ? "Aucun résultat" : "Aucun devis"}
            text={appliedSearch
              ? "Aucun devis ne correspond à votre recherche."
              : "Les devis créés sur ordinateur apparaîtront ici."}
          />
        ) : (
          <div className="list">
            {rows.map((r) => {
              const state = proformaPill(r);
              return (
                <button key={r.id} className="rcard" onClick={() => openDetail(r)}>
                  <Mono name={r.invoice_number || "D"} />
                  <div className="rcard__main">
                    <div className="rcard__name">{r.invoice_number}</div>
                    <div className="rcard__meta">
                      {r.is_deleted ? <Pill tone="danger" label="Supprimé" /> : null}
                      <Pill tone={state.tone} label={state.label} />
                    </div>
                    <div className="rcard__meta">
                      <span className="rcard__sub">{fmtDate(r.order_date)} · {clientNameOf(r, clients)}</span>
                    </div>
                  </div>
                  <span className="rcard__price">{fmtMoney(r.total)}</span>
                </button>
              );
            })}

            {rows.length < total ? (
              <button className="btn btn--ghost" style={{ width: "100%" }} onClick={loadMore} disabled={loading}>
                <Ico name={loading ? "loader-circle" : "list-ordered"} className={loading ? "spin" : ""} />
                Charger plus ({rows.length}/{total})
              </button>
            ) : null}
          </div>
        )}
      </div>

      <Sheet open={navOpen} onClose={() => setNavOpen(false)} title="Aller à…" subtitle="Autres écrans du module Ventes.">
        {salesTabs.map((t) => (
          <ActionRow key={t.key} icon="chevron-right" label={t.label}
            onClick={() => { setNavOpen(false); navigate(t.path); }} />
        ))}
      </Sheet>

      {/* feuille : détail */}
      <Sheet
        open={Boolean(detail)} onClose={closeDetail}
        title={detail?.invoice_number}
        subtitle={detail ? clientNameOf(detail, clients) : undefined}
      >
        {detail ? (
          <>
            <DetailRow label="Date" value={fmtDate(detail.order_date)} />
            <DetailRow label="Établissement" value={detail.warehouse_name || `Établissement #${detail.warehouse_id}`} />
            <DetailRow label="Statut" value={proformaPill(detail).label} />
            <DetailRow label="Montant HT" value={fmtMoney(detail.subtotal)} />
            <DetailRow label="Taxes" value={fmtMoney(detail.tax_amount)} />
            <DetailRow label="Montant TTC" value={fmtMoney(detail.total)} />
            {detail.is_converted && detail.sale_invoice_number ? (
              <DetailRow label="Vente créée" value={detail.sale_invoice_number} />
            ) : null}

            <div className="field__lbl" style={{ margin: "16px 0 8px" }}>Articles</div>
            {detailLoading && !items.length ? (
              <EmptyState loading title="Chargement…" />
            ) : !items.length ? (
              <Note>Aucun article à afficher.</Note>
            ) : (
              items.map((it, i) => (
                <DetailRow key={it.id ?? i}
                  label={it.product_name || it.name || `Produit #${it.product_id}`}
                  value={`${fmt(it.quantity)} × ${fmtMoney(it.unit_price)}`} />
              ))
            )}

            {detail.notes ? (
              <>
                <div className="field__lbl" style={{ margin: "16px 0 8px" }}>Remarques</div>
                <Note>{detail.notes}</Note>
              </>
            ) : null}

            <div style={{ marginTop: 16 }}>
              {actions.convert ? (
                <ActionRow icon="arrow-right-left" label="Convertir en vente" onClick={() => convert(detail)} />
              ) : null}
              <ActionRow icon={pdfBusy ? "loader-circle" : "printer"}
                label={pdfBusy ? "Génération…" : "Télécharger le PDF"} onClick={() => downloadPdf(detail)} />
              {actions.restore ? (
                <ActionRow icon="rotate-ccw" label="Restaurer le devis" onClick={() => runDestructive(detail, "restore")} />
              ) : null}
              {actions.delete ? (
                <ActionRow icon="trash-2" label="Supprimer le devis" danger onClick={() => runDestructive(detail, "delete")} />
              ) : null}
            </div>

            {num(detail.is_converted) ? null : (
              <Note>La création et la modification d'un devis se font sur ordinateur.</Note>
            )}
          </>
        ) : null}
      </Sheet>
    </ProductScreen>
  );
}
